import React, { useState, useEffect } from "react";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { Plus, X, ChefHat, Loader2, Sparkles } from "lucide-react";
import { GOALS } from "./_shared";
import MealPlanView from "./MealPlanView";
import CustomizerChecklist from "./CustomizerChecklist";

export default function NutritionTab({ me, goal, setGoal, foodsByGoal, setFoodsByGoal, plan, setPlan, refreshMe }) {
  const [foodInput, setFoodInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [savingFoods, setSavingFoods] = useState(false);
  const [opts, setOpts] = useState({});
  const setOpt = (id, value) => setOpts((o) => ({ ...o, [id]: value }));
  const quota = me?.quotas?.meal_plans || { used: 0, limit: 3, resets_at: null };
  const unlimited = quota.limit === null || quota.limit === undefined;
  const reachedLimit = !unlimited && quota.used >= quota.limit;
  const resetsLabel = quota.resets_at
    ? new Date(quota.resets_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  useEffect(() => {
    memberFetch(`/member/suggested-foods?goal=${goal}`).then((r) => setSuggestions(r.foods || [])).catch(() => setSuggestions([]));
  }, [goal]);

  const currentFoods = foodsByGoal[goal] || [];
  const addFood = (v) => {
    const raw = (v || foodInput).trim();
    if (!raw) return;
    // Auto-split commas / line breaks so "beef, chicken, oxtail" → 3 chips
    const tokens = raw.split(/[,\n]+/).map((t) => t.trim()).filter(Boolean);
    if (tokens.length === 0) { setFoodInput(""); return; }
    const merged = [...currentFoods];
    for (const t of tokens) {
      if (!merged.includes(t)) merged.push(t);
    }
    setFoodsByGoal({ ...foodsByGoal, [goal]: merged.slice(0, 30) });
    setFoodInput("");
  };
  const removeFood = (f) => setFoodsByGoal({ ...foodsByGoal, [goal]: currentFoods.filter((x) => x !== f) });

  const saveFoods = async () => {
    setSavingFoods(true);
    try {
      await memberFetch("/member/foods", { method: "PUT", body: JSON.stringify({ goal, foods: currentFoods }) });
      toast.success("Favorites saved");
    } catch (e) { toast.error(e.message); }
    finally { setSavingFoods(false); }
  };

  const generate = async () => {
    if (reachedLimit) {
      toast.error(`You've used all ${quota.limit} meal-plan generations this 30-day window. Resets ${resetsLabel || "soon"}.`);
      return;
    }
    setGenerating(true);
    setPlan(null);
    try {
      await memberFetch("/member/foods", { method: "PUT", body: JSON.stringify({ goal, foods: currentFoods }) });
      const body = { goal };
      if (opts.age) body.age = Number(opts.age) || undefined;
      if (opts.height_in) body.height_in = Number(opts.height_in) || undefined;
      if (opts.weight_lb) body.weight_lb = Number(opts.weight_lb) || undefined;
      if (opts.vegan) body.vegan = true;
      if (opts.allergies && typeof opts.allergies === "string") body.allergies = opts.allergies;
      if (opts.budget_friendly) body.budget_friendly = true;
      const data = await memberFetch("/member/meal-plan", { method: "POST", body: JSON.stringify(body) });
      setPlan(data);
      if (refreshMe) await refreshMe();
      toast.success("12 flavorful meals ready");
    } catch (e) { toast.error(e.message || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const notAdded = suggestions.filter((s) => !currentFoods.includes(s)).slice(0, 16);

  return (
    <div data-testid="tab-nutrition">
      {/* Goal selector */}
      <div className="flex gap-1 border-b border-zinc-800 mb-8 overflow-x-auto">
        {GOALS.map((g) => (
          <button
            key={g.id}
            onClick={() => setGoal(g.id)}
            data-testid={`goal-tab-${g.id}`}
            className={`px-5 py-3 text-xs font-bold tracking-[0.2em] uppercase whitespace-nowrap border-b-2 transition-colors ${goal === g.id ? "border-[#E5B43A] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
          >{g.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-7 border border-zinc-800 bg-[#141414] p-6 md:p-8" data-testid="favorites-card">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">{GOALS.find(g => g.id === goal)?.label}</div>
          <h2 className="font-display text-2xl mb-2">Your Favorite Foods</h2>
          <p className="text-zinc-500 text-sm mb-5">{GOALS.find(g => g.id === goal)?.desc}.</p>

          {/* Quick-add chips from suggestions */}
          {notAdded.length > 0 && (
            <div className="mb-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Quick Add · Tap to include</div>
              <div className="flex flex-wrap gap-2">
                {notAdded.map((s) => (
                  <button
                    key={s}
                    onClick={() => addFood(s)}
                    data-testid={`suggested-food-${s}`}
                    className="inline-flex items-center gap-1 border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
                  ><Plus size={11} /> {s}</button>
                ))}
              </div>
            </div>
          )}

          {/* Type to add */}
          <form onSubmit={(e) => { e.preventDefault(); addFood(); }} className="flex gap-2 mb-5">
            <input
              value={foodInput}
              onChange={(e) => setFoodInput(e.target.value)}
              placeholder="Type a food (separate with commas to add several)..."
              data-testid="food-input"
              className="flex-1 bg-[#0F0F0F] border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-[#E5B43A]"
            />
            <button type="submit" data-testid="add-food-btn" className="bg-[#E5B43A] text-black px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors flex items-center gap-2">
              <Plus size={14} /> Add
            </button>
          </form>

          <div className="border-t border-zinc-800 pt-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Your Picks ({currentFoods.length}/30)</div>
            {currentFoods.length === 0 ? (
              <p className="text-zinc-600 text-sm italic">No favorites yet. Tap suggestions above or type your own.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {currentFoods.map((f) => (
                  <span key={f} className="inline-flex items-center gap-2 border border-[#E5B43A]/70 bg-[#E5B43A]/10 px-3 py-2 text-sm text-white max-w-full break-all" data-testid={`food-chip-${f}`}>
                    <span className="break-all">{f}</span>
                    <button onClick={() => removeFood(f)} className="text-zinc-300 hover:text-[#E5B43A] shrink-0"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {currentFoods.length > 0 && (
            <button onClick={saveFoods} disabled={savingFoods} className="mt-5 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-[#E5B43A] disabled:opacity-50" data-testid="save-foods-btn">
              {savingFoods ? "Saving..." : "Save Favorites"}
            </button>
          )}
        </div>

        <div className="lg:col-span-5 border border-[#E5B43A] bg-[#141414] p-6 md:p-8 flex flex-col" data-testid="generate-card">
          <ChefHat className="text-[#E5B43A] mb-4" size={28} />
          <h2 className="font-display text-2xl mb-3">Generate 12 Meals</h2>
          <p className="text-zinc-400 text-sm flex-1 leading-relaxed">
            Bold, flavorful meals built for <b className="text-white">{GOALS.find(g => g.id === goal)?.label}</b>, with cooking instructions. ~30-60s.
          </p>

          <div className="mt-4 border border-zinc-800 bg-[#0F0F0F] px-3 py-2 flex items-center justify-between text-[10px] tracking-[0.2em] uppercase" data-testid="meal-quota">
            <span className="text-zinc-500">Generations</span>
            <span className={reachedLimit ? "text-[#E5B43A] font-bold" : "text-zinc-300 font-bold"}>
              {unlimited ? "Unlimited" : `${quota.used} / ${quota.limit}${resetsLabel ? ` · resets ${resetsLabel}` : ""}`}
            </span>
          </div>

          <button
            onClick={generate}
            disabled={generating || reachedLimit}
            data-testid="generate-meal-plan-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#E5B43A] disabled:hover:text-white"
          >
            {generating
              ? <><Loader2 className="animate-spin" size={16} /> Cooking...</>
              : reachedLimit
                ? <>Limit Reached</>
                : <><Sparkles size={16} /> {quota.used > 0 ? "Replace Current Plan" : "Generate Plan"}</>}
          </button>
          {plan && !reachedLimit && quota.used > 0 && (
            <p className="mt-2 text-[10px] tracking-widest uppercase text-zinc-500">Generating again will replace the plan below.</p>
          )}

          <div className="mt-3">
            <CustomizerChecklist
              testIdPrefix="meal-opts"
              title="Personalize this plan"
              values={opts}
              onChange={setOpt}
              options={[
                { id: "age", kind: "text", inputType: "number", label: "Age",
                  placeholder: "e.g. 32" },
                { id: "height_in", kind: "text", inputType: "number", label: "Height (inches)",
                  placeholder: "e.g. 70" },
                { id: "weight_lb", kind: "text", inputType: "number", label: "Bodyweight (lb)",
                  placeholder: "e.g. 175" },
                { id: "vegan", kind: "check", label: "Vegan only",
                  hint: "No animal products of any kind." },
                { id: "allergies", kind: "check", label: "I have allergies",
                  hint: "We'll never include these ingredients.",
                  requiresInput: true,
                  placeholder: "e.g. peanuts, shellfish, gluten" },
                { id: "budget_friendly", kind: "check", label: "Budget-friendly",
                  hint: "Cost-effective, easy-to-source staples." },
              ]}
            />
          </div>
        </div>
      </div>

      {plan && <MealPlanView plan={plan} />}
    </div>
  );
}
